import { useEffect, useState } from 'react';
import usePrevious from '../../hooks/usePrevious';
import {
  fetchComposition,
  getFetchUrl,
  handleFocusAfterPageNavigation,
  handleScrollPositionAfterPageNavigation,
  LocationStateDefined,
  needsReFetch,
  State,
} from './browser.utils';
import Event from '../../lib/EventEmitter';
import { reloadCurrentLocation } from '../../lib/window/Window';
import { Composition } from '../../types/api/Composition';
import { windowLogger } from '../../_lib/Logger/windowLogger';
import { AxiosHeaders, RawAxiosResponseHeaders } from 'axios';

function isAxiosHeaders(
  headers: RawAxiosResponseHeaders | (RawAxiosResponseHeaders & AxiosHeaders),
): headers is AxiosHeaders {
  return typeof (headers as AxiosHeaders).hasContentType === 'function';
}

export function useCompositionFetcher(composition: Composition, state: State) {
  const [fetchedComposition, setFetchedComposition] = useState<Composition>(() => composition);

  const prevLocation = usePrevious(state.location);
  const fetchUrl = getFetchUrl(state.location as LocationStateDefined, composition);

  useEffect(() => {
    if (needsReFetch(state.location as LocationStateDefined, prevLocation as LocationStateDefined)) {
      Event.emit(Event.loadingFeedback.start);
      fetchComposition(fetchUrl, composition.publicSessionKey)
        .then((response) => {
          // The backend will refuse a valid composition request with code 419 when the requested publicSessionKey
          // does not match the publicSessionKey of the actual backend session (i.e. the logged-in user has changed).
          // => Throw an error to trigger a regular navigation, refreshing the whole page (incl. SPA-omitted sections)
          if (response.status === 419) {
            throw new Error('session expired (code 419), refused SPA request');
          }

          // Only if we get a SON-JSON from the response, we want to save the data into state.
          if (isAxiosHeaders(response.headers) && response.headers.hasContentType('application/json')) {
            setFetchedComposition(response.data);
            Event.emit(Event.loadingFeedback.stop);
          } else {
            throw Error('Composition did not provide a JSON');
          }
        })
        .catch((error) => {
          windowLogger.warn(error);
          reloadCurrentLocation();
        });
    }
  }, [composition.publicSessionKey, fetchUrl, prevLocation, state.location]);

  /**
   * Handling memorized scroll position after navigation and focussing <h1>.
   * Testing (see FocusingHeadlineOnPageRefresh.cy.js):
   * - Homepage: scroll down to a link => klick => go back => scroll position is where you left, focus is on <h1>.
   * - Reload: scroll position moves to where you left, focus is not on <h1>.
   * - Open Link in new window => tab once => first skip link is focused.
   * BEWARE: state.location is not included in dependency array here, because of scroll position issues!
   */
  useEffect(() => {
    handleScrollPositionAfterPageNavigation(state.location);
    // focusing h1 only, if we are not doing a real refresh like reload or link open in new window
    if (fetchedComposition !== composition) {
      handleFocusAfterPageNavigation();
    }
    // state.location is not included in dependency array here, because of scroll position issues!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composition, fetchedComposition]);

  return { fetchedComposition };
}
